import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserTable, User } from '@/components/users/UserTable';
import { AddUserDialog, UserWithoutId } from '@/components/users/AddUserDialog';
import { useToast } from '@/components/ui/use-toast';
import { Separator } from '@/components/ui/separator';
import { 
  fetchUsers, 
  createUser, 
  deleteUser as apiDeleteUser, 
  updateUserStatus as apiUpdateUserStatus 
} from '@/services/userService';
import { useQuery } from "@tanstack/react-query";

export default function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const { toast } = useToast();

  const { data: usersData = [], isLoading, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  });


  const addUser = async (user: UserWithoutId) => {
    try {
      const newUser = await createUser(user);
      setUsers([...users, newUser]);
      toast({
        title: "User added",
        description: `${user.name} has been added successfully.`
      });
      setIsAddUserOpen(false);
      refetch();
    } catch (err) {
      console.error("Failed to add user:", err);
      toast({
        title: "Error",
        description: "Failed to add user. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      await apiDeleteUser(userId);
      setUsers(users.filter(user => user._id !== userId));
      toast({
        title: "User deleted",
        description: "The user has been deleted successfully."
      });
      refetch();
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    }
  };

  const updateUserStatus = async (userId: string, status: 'active' | 'inactive') => {
    try {
      await apiUpdateUserStatus(userId, status);
      setUsers(users.map(user => 
        user._id === userId ? { ...user, active: status === 'active' } : user
      ));
      
      toast({
        title: "User updated",
        description: `User status changed to ${status}.`
      });
      refetch();
    } catch (err) {
      console.error("Failed to update user status:", err);
      toast({
        title: "Error",
        description: "Failed to update user status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredUsers = activeTab === "all" 
    ? usersData
    : usersData.filter(user => user.role === activeTab);

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">
            Manage user accounts and permissions
          </p>
        </div>
        <Button onClick={() => setIsAddUserOpen(true)}>
          Add New User
        </Button>
      </div>
      
      <Separator className="my-6" />
      
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            View and manage all users in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="p-4 text-center text-red-500">{error.message}</div>
          ) : isLoading ? (
            <div className="p-8 text-center">Loading users...</div>
          ) : (
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="all">All Users</TabsTrigger>
                <TabsTrigger value="super_admin">Super Admins</TabsTrigger>
                <TabsTrigger value="hrm">HR Managers</TabsTrigger>
                <TabsTrigger value="talent_aquisition">Talent Acquisition</TabsTrigger>
                <TabsTrigger value="employee">Employees</TabsTrigger>
              </TabsList>
              <TabsContent value={activeTab}>
                <UserTable 
                  users={filteredUsers}
                  onDelete={deleteUser}
                  onStatusChange={updateUserStatus}
                  onUpdate={refetch}
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      <AddUserDialog 
        open={isAddUserOpen} 
        onOpenChange={setIsAddUserOpen}
        onAddUser={addUser}
      />
    </div>
  );
} 